import { GoPlus, GoX, GoChevronDown } from "react-icons/go";
import { HiMenuAlt2 } from "react-icons/hi";
import { useState, useEffect } from "react";
import { Combobox } from "@headlessui/react";

/**
 * Displays the keyword filter for the data explorer
 */
export default function ExplorerKeywordFilter(props) {
  const [inputValue, setInputValue] = useState("");
  const [selectedKeyword, setSelectedKeyword] = useState("");
  const [allowInputValueChange, setAllowInputValueChange] = useState(true);
  const keywordFilters = props.filters;
  const setKeywordFilters = props.setFilters;

  //Takes keywordChoices and filters them based on what the user has entered into the keyword search input
  const filteredKeywordChoices =
    inputValue === ""
      ? props.keywordChoices
      : props.keywordChoices.filter((keywordChoice) => {
        return keywordChoice.toLowerCase().includes(inputValue.toLowerCase());
      });

  //Populates the combobox input with the user's keyword selection from the combobox dropdown
  useEffect(() => {
    if (
      typeof selectedKeyword !== "undefined" &&
      selectedKeyword !== "" &&
      selectedKeyword !== null
    ) {
      // Without this, when you select an item from the dropdown, it adds a chip for the item but then also populates the input (bad). 
      // This makes it so the chip is added and the input remains empty by checking if input value is allowed to be added.
      if (allowInputValueChange) {
        setInputValue(selectedKeyword);
      }
      setAllowInputValueChange(true);
    }
  }, [selectedKeyword]);

  /**
   * Adds a keyword to the filter
   */
  function addKeyword(keyword) {
    setSelectedKeyword("");
    let trimmedInputValue;

    if (keyword !== undefined) {
      trimmedInputValue = keyword.trim();
    } else {
      trimmedInputValue = JSON.parse(JSON.stringify(inputValue)).trim()
    }

    if (trimmedInputValue !== "" && !keywordFilters.includes(trimmedInputValue)) {
      setKeywordFilters((prevState) => {
        const filters = [...prevState, trimmedInputValue]

        return filters;
      });
      setInputValue("");
    }
  }

  /**
   * Removes the specified keyword from the filter
   *
   * @param {*} keyword Keyword to remove.
   */
  function deleteKeyword(keyword) {
    let updatedKeywordsFilter = [...keywordFilters];
    updatedKeywordsFilter.splice(updatedKeywordsFilter.indexOf(keyword), 1);

    setKeywordFilters(updatedKeywordsFilter);
  }

  //Joins strings. Used for combobox option hover styles.
  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  return (
    <div className="absolute top-3 ml-3 mr-3 flex-col max-w-sm">
      <div className="flex items-center gap-y-2 z-20 bg-white rounded-xl border border-gray-300 w-[16rem] shadow-sm pl-4 pr-1 py-1 dark:border-gray-700 dark:bg-gray-800">
        <HiMenuAlt2 className="h-6 w-6 text-blue-600 dark:text-blue-500" />
        <Combobox
          as="div"
          className="flex-1"
          value={selectedKeyword}
          onChange={setSelectedKeyword}
          nullable
        >
          <div className="relative">
            <Combobox.Input
              autoComplete="off"
              value={inputValue}
              data-cy="keywords"
              className="w-full bg-white border-0 focus:outline-none focus:ring-0 text-sm placeholder:text-gray-600 text-gray-900 font-medium dark:bg-gray-800 dark:text-white dark:placeholder-gray-200"
              onChange={(event) => {
                setInputValue(event.target.value);
              }}
              displayValue={inputValue}
              maxLength={250}
              placeholder="Filter by Keywords"
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  addKeyword(e.target.value);
                  setSelectedKeyword("")
                  setInputValue("");
                }
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                  // setInputValue("");
                }
              }}
            />

            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
              <GoChevronDown
                className="h-6 w-6 text-gray-400"
                aria-hidden="true"
              />
            </Combobox.Button>

            {filteredKeywordChoices.length > 0 && (
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-gray-800 dark:border-gray-700">
                {filteredKeywordChoices.map((filteredKeywordChoice, index) => (
                  <Combobox.Option
                    key={filteredKeywordChoice || `keyword-${index}`}
                    onClick={(e) => {
                      addKeyword(filteredKeywordChoice)
                      setAllowInputValueChange(false);
                    }}
                    value={filteredKeywordChoice}
                    className={({ active }) =>
                      classNames(
                        "relative cursor-default select-none py-2 pl-3 pr-9",
                        active ? "bg-blue-600 text-white" : "text-gray-900 dark:text-gray-200"
                      )
                    }
                  >
                    <span>{filteredKeywordChoice}</span>
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </div>
        </Combobox>

      </div>
      <div className="flex items-start flex-wrap gap-y-2 z-20 mt-2">
        {keywordFilters !== undefined && (
          keywordFilters.map((keywordFilter) => {
            return (
              <span
                className="mr-1.5 px-3 py-2 rounded-lg text-white bg-blue-700 font-semibold text-sm flex items-center w-max cursor-pointer active:bg-gray-300 transition duration-300 ease hover:bg-blue-600"
                key={keywordFilter}
                onClick={() => {
                  deleteKeyword(keywordFilter);
                }}
              >
                {keywordFilter}
                <GoX className="h-4 w-4 text-white ml-2" aria-hidden="true" />
              </span>
            );
          })
        )}
      </div>
    </div>
  );
}